import React, { useState } from 'react';
import { useEntityList, ENTITIES, createEntity, updateEntity, deleteEntity, useTenant, isPrivileged } from '../data/entities';
import { kitEnv } from '../data/kitEnv';

// Embedded agent identities: what each agent may READ, what it may PROPOSE
// to write (always confirm-gated), its prompt, and its embed snippet. The
// backend (api/agentServer.js) enforces the allowlists per tool dispatch —
// this page only edits the identity doc.
const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

// Entities an embedded agent could plausibly be scoped to (registry entries
// exposed to agent tools, minus the copilot-internal ones).
const SCOPE_ENTITIES = Object.keys(ENTITIES).filter(
  (name) => !['aiSession', 'aiMessage', 'reviewItem', 'commit', 'agent'].includes(name)
);

const newToken = () =>
  `agt_${Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('')}`;

function AgentCard({ agent }) {
  const [draft, setDraft] = useState(null); // null = view mode
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const snippet = API
    ? `<script src="${API}/agent/embed.js?token=${agent.publicToken}" async></script>`
    : '(set REACT_APP_UPLOAD_API to show the embed snippet)';

  const save = async () => {
    setErr('');
    try {
      await updateEntity('agent', {}, agent.id, draft);
      setDraft(null);
    } catch (e) {
      setErr(e.message);
    }
  };

  const toggleList = (field, name) => {
    const cur = draft[field] || [];
    setDraft({ ...draft, [field]: cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name] });
  };

  return (
    <div className="cs-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ color: 'var(--text)' }}>{agent.name}</strong>
        {!agent.enabled && <span className="cs-help" style={{ fontSize: 12 }}>disabled</span>}
        <span style={{ flex: 1 }} />
        {draft === null ? (
          <>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setDraft({
              name: agent.name,
              enabled: agent.enabled !== false,
              systemPrompt: agent.systemPrompt || '',
              greeting: agent.greeting || '',
              allowReads: agent.allowReads || [],
              allowWrites: agent.allowWrites || [],
            })}>
              Edit
            </button>
            <button
              className="cs-btn cs-btn-outline cs-btn-sm"
              onClick={() => {
                if (window.confirm(`Delete agent "${agent.name}"?`)) deleteEntity('agent', {}, agent.id);
              }}
            >
              Delete
            </button>
          </>
        ) : (
          <>
            <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={save}>Save</button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setDraft(null)}>Cancel</button>
          </>
        )}
      </div>

      {draft === null ? (
        <>
          <p className="cs-help" style={{ margin: '8px 0 0', fontSize: 12 }}>
            reads: {(agent.allowReads || []).join(', ') || 'knowledge only'} · proposes writes to:{' '}
            {(agent.allowWrites || []).join(', ') || 'nothing'}
          </p>
          {agent.memory && (
            <details style={{ marginTop: 8 }}>
              <summary className="cs-help" style={{ fontSize: 12, cursor: 'pointer' }}>Long-term memory</summary>
              <p className="cs-help" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{agent.memory}</p>
            </details>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
            <code style={{ fontSize: 11.5, overflow: 'auto', whiteSpace: 'nowrap', flex: 1 }}>{snippet}</code>
            {API && (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(snippet).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
            )}
          </div>
          <p className="cs-help" style={{ margin: '6px 0 0', fontSize: 12 }}>
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => {
                if (window.confirm('Rotate the public token? Existing embeds stop working until updated.')) {
                  updateEntity('agent', {}, agent.id, { publicToken: newToken() }).catch((e) => setErr(e.message));
                }
              }}
            >
              Rotate token
            </button>
          </p>
        </>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
          <input className="cs-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <label className="cs-help" style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />{' '}
            Enabled
          </label>
          <input
            className="cs-input"
            placeholder="Greeting shown to visitors"
            value={draft.greeting}
            onChange={(e) => setDraft({ ...draft, greeting: e.target.value })}
          />
          <textarea
            className="cs-input"
            style={{ minHeight: 90 }}
            placeholder="System prompt (persona + guardrails live in the allowlists, not here)"
            value={draft.systemPrompt}
            onChange={(e) => setDraft({ ...draft, systemPrompt: e.target.value })}
          />
          <div>
            <span className="cs-help" style={{ fontSize: 12 }}>May read:</span>{' '}
            {SCOPE_ENTITIES.map((name) => (
              <label key={name} className="cs-help" style={{ fontSize: 12, marginRight: 10 }}>
                <input
                  type="checkbox"
                  checked={(draft.allowReads || []).includes(name)}
                  onChange={() => toggleList('allowReads', name)}
                />{' '}
                {name}
              </label>
            ))}
          </div>
          <div>
            <span className="cs-help" style={{ fontSize: 12 }}>May propose writes to (confirm-gated):</span>{' '}
            {SCOPE_ENTITIES.map((name) => (
              <label key={name} className="cs-help" style={{ fontSize: 12, marginRight: 10 }}>
                <input
                  type="checkbox"
                  checked={(draft.allowWrites || []).includes(name)}
                  onChange={() => toggleList('allowWrites', name)}
                />{' '}
                {name}
              </label>
            ))}
          </div>
        </div>
      )}
      {err && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{err}</p>}
    </div>
  );
}

export default function Agents() {
  const { docs: agents, error } = useEntityList('agent', {});
  // Agents speak for the tenant — only org admins/owners create them (rules enforce).
  const tenant = useTenant();
  const canCreate = !tenant.orgId || isPrivileged(tenant.role);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await createEntity('agent', {}, { name: name.trim(), publicToken: newToken() });
      setName('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cs-page">
      <h1 style={{ marginBottom: 4 }}>Agents</h1>
      <p className="cs-help">
        Chat agents you can drop onto any website with one snippet. Each agent only reads what you allow,
        can only propose changes (a visitor confirms them), and every action is logged.
      </p>

      {canCreate && (
      <form onSubmit={create} style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
        <input
          className="cs-input"
          style={{ maxWidth: 320 }}
          placeholder="New agent name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!name.trim() || busy}>
          Create
        </button>
      </form>
      )}

      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{error}</p>}
      {agents === null && <p className="cs-help">Loading…</p>}
      {agents?.length === 0 && (
        <div className="cs-empty">
          No agents yet. Create one above — you&apos;ll get an embed snippet and a place to set what it may read.
        </div>
      )}
      <div style={{ display: 'grid', gap: 12 }}>
        {(agents || []).map((a) => <AgentCard key={a.id} agent={a} />)}
      </div>
    </div>
  );
}
